import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    App as AntdApp,
    Button,
    Card,
    Drawer,
    Form,
    Input,
    InputNumber,
    Popconfirm,
    Radio,
    Select,
    Space,
    Table,
    Tooltip,
    Typography,
} from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { RealtimeConfig, RealtimeConfigValueType, RealtimeConfigVersion } from '../entities/RealtimeConfig';
import { RealtimeConfigService } from '../http/RealtimeConfigService';
import './RealtimeConfigPage.css';

const { Text, Title } = Typography;

const DEFAULT_APP_KEY = 'com.haiskynology.sjreet.mall';
const VALUE_TYPE_OPTIONS: Array<{ label: RealtimeConfigValueType; value: RealtimeConfigValueType }> = [
    { label: 'STRING', value: 'STRING' },
    { label: 'INTEGER', value: 'INTEGER' },
    { label: 'DOUBLE', value: 'DOUBLE' },
    { label: 'BOOL', value: 'BOOL' },
    { label: 'MAP', value: 'MAP' },
    { label: 'LIST', value: 'LIST' },
];

const CATEGORY_OPTIONS = ['GENERAL', 'PHONE', 'ADDRESS', 'BIRTHDAY'].map((value) => ({ label: value, value }));

type RealtimeConfigFormValues = {
    appKey: string;
    category: RealtimeConfig['category'];
    key: string;
    valueType: RealtimeConfigValueType;
    valueText: string | number | null;
    description?: string;
};

function stringifyConfigValue(value: unknown) {
    if (value == null) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    return JSON.stringify(value, null, 2);
}

function normalizeConfigValueForForm(value: unknown, valueType: RealtimeConfigValueType) {
    if (value == null) {
        return getDefaultValueForType(valueType);
    }
    if (valueType === 'INTEGER' || valueType === 'DOUBLE') {
        const numberValue = typeof value === 'number' ? value : Number(value);
        return Number.isFinite(numberValue) ? numberValue : null;
    }
    if (valueType === 'BOOL') {
        return String(value === true || value === 'true');
    }
    return stringifyConfigValue(value);
}

function getDefaultValueForType(valueType: RealtimeConfigValueType) {
    if (valueType === 'INTEGER' || valueType === 'DOUBLE') {
        return null;
    }
    if (valueType === 'BOOL') {
        return 'true';
    }
    if (valueType === 'MAP') {
        return '{}';
    }
    if (valueType === 'LIST') {
        return '[]';
    }
    return '';
}

function parseConfigValue(valueText: string | number | null, valueType: RealtimeConfigValueType) {
    const text = String(valueText ?? '').trim();
    if (valueType === 'INTEGER') {
        const value = Number.parseInt(text, 10);
        if (!Number.isFinite(value)) {
            throw new Error('invalid integer');
        }
        return value;
    }
    if (valueType === 'DOUBLE') {
        const value = Number.parseFloat(text);
        if (!Number.isFinite(value)) {
            throw new Error('invalid double');
        }
        return value;
    }
    if (valueType === 'BOOL') {
        return text === 'true';
    }
    if (valueType === 'MAP' || valueType === 'LIST') {
        return text ? JSON.parse(text) : valueType === 'LIST' ? [] : {};
    }
    return valueText;
}

function getConfigIdentity(config: RealtimeConfig) {
    return {
        appKey: config.appKey,
        category: config.category,
        key: config.key,
    };
}

function getErrorMessage(error: unknown, fallback: string) {
    return error instanceof Error ? error.message : fallback;
}

export default function RealtimeConfigPage() {
    const { message, modal } = AntdApp.useApp();
    const [form] = Form.useForm<RealtimeConfigFormValues>();
    const [configs, setConfigs] = useState<RealtimeConfig[]>([]);
    const [appKeys, setAppKeys] = useState<string[]>([]);
    const [filters, setFilters] = useState<{ appKey?: string; category?: string; key?: string }>({ category: 'GENERAL' });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [editingConfig, setEditingConfig] = useState<RealtimeConfig | null>(null);
    const [historyConfig, setHistoryConfig] = useState<RealtimeConfig | null>(null);
    const [historyVersions, setHistoryVersions] = useState<RealtimeConfigVersion[]>([]);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: 20,
        total: 0,
        showSizeChanger: true,
    });

    const selectedValueType = Form.useWatch('valueType', form) ?? 'STRING';
    const drawerConfigKey = Form.useWatch('key', form) ?? editingConfig?.key ?? '';
    const appKeyOptions = useMemo(
        () => appKeys.map((value) => ({ label: value, value, title: value })),
        [appKeys]
    );

    const copyText = useCallback(
        async (text: string, label = '内容') => {
            try {
                await navigator.clipboard.writeText(text);
                message.success(`${label}已复制`);
            } catch {
                message.error(`${label}复制失败`);
            }
        },
        [message]
    );

    const loadConfigs = useCallback(
        async (pageNum = 1, pageSize = 20, nextFilters = filters) => {
            setLoading(true);
            try {
                const result = await RealtimeConfigService.listRealtimeConfigs({
                    pageNum,
                    pageSize,
                    includeEncrypted: false,
                    ...nextFilters,
                });
                setConfigs(result.list ?? []);
                setPagination((current) => ({
                    ...current,
                    current: result.page?.pageNum ?? pageNum,
                    pageSize: result.page?.pageSize ?? pageSize,
                    total: result.page?.totalCount ?? 0,
                }));
            } catch (error) {
                message.error(getErrorMessage(error, '获取配置失败'));
            } finally {
                setLoading(false);
            }
        },
        [filters, message]
    );

    const loadAppKeys = useCallback(async () => {
        try {
            const list = await RealtimeConfigService.listRealtimeConfigAppKeys();
            setAppKeys(list);
            setFilters((current) => current.appKey ? current : { ...current, appKey: list[0] ?? DEFAULT_APP_KEY, category: current.category ?? 'GENERAL' });
        } catch (error) {
            message.error(getErrorMessage(error, '获取 AppKey 失败'));
        }
    }, [message]);

    useEffect(() => {
        void loadAppKeys();
    }, [loadAppKeys]);

    useEffect(() => {
        if (!filters.appKey) {
            return;
        }
        void loadConfigs(1, pagination.pageSize ?? 20, filters);
    }, [filters, loadConfigs, pagination.pageSize]);

    const openCreateDrawer = useCallback(() => {
        setEditingConfig(null);
        form.setFieldsValue({
            appKey: filters.appKey ?? appKeys[0] ?? DEFAULT_APP_KEY,
            category: 'GENERAL',
            key: '',
            valueType: 'STRING',
            valueText: getDefaultValueForType('STRING'),
            description: '',
        });
        setDrawerOpen(true);
    }, [appKeys, filters.appKey, form]);

    const openEditDrawer = useCallback(
        (record: RealtimeConfig) => {
            setEditingConfig(record);
            form.setFieldsValue({
                appKey: record.appKey,
                category: record.category,
                key: record.key,
                valueType: record.valueType ?? 'STRING',
                valueText: normalizeConfigValueForForm(record.value, record.valueType ?? 'STRING'),
                description: record.description ?? '',
            });
            setDrawerOpen(true);
        },
        [form]
    );

    const saveConfig = useCallback(async () => {
        const values = await form.validateFields();
        let parsedValue: unknown;
        try {
            parsedValue = parseConfigValue(values.valueText, values.valueType);
        } catch {
            message.error('配置值不是合法 JSON');
            return;
        }
        setSaving(true);
        try {
            const payload: RealtimeConfig = {
                id: editingConfig?.id,
                appKey: values.appKey.trim(),
                category: values.category,
                key: values.key.trim(),
                valueType: values.valueType,
                value: parsedValue,
                description: values.description?.trim() || undefined,
            };
            if (editingConfig) {
                await RealtimeConfigService.updateRealtimeConfig(payload);
                message.success('配置已更新');
            } else {
                await RealtimeConfigService.insertRealtimeConfig(payload);
                message.success('配置已创建');
            }
            setDrawerOpen(false);
            await loadConfigs();
            void loadAppKeys();
        } catch (error) {
            message.error(getErrorMessage(error, '保存配置失败'));
        } finally {
            setSaving(false);
        }
    }, [editingConfig, form, loadAppKeys, loadConfigs, message]);

    const deleteConfig = useCallback(
        async (record: RealtimeConfig) => {
            setLoading(true);
            try {
                await RealtimeConfigService.deleteRealtimeConfig(getConfigIdentity(record));
                message.success('配置已删除');
                await loadConfigs();
                void loadAppKeys();
            } catch (error) {
                message.error(getErrorMessage(error, '删除配置失败'));
            } finally {
                setLoading(false);
            }
        },
        [loadAppKeys, loadConfigs, message]
    );

    const rollbackConfig = useCallback(
        (version: RealtimeConfigVersion) => {
            if (!historyConfig || version.version == null) {
                return;
            }
            modal.confirm({
                title: `回滚到 v${version.version}？`,
                content: '回滚会立即发布旧版本配置，并生成新的当前版本。',
                onOk: async () => {
                    await RealtimeConfigService.rollbackRealtimeConfig({
                        ...getConfigIdentity(historyConfig),
                        version: version.version,
                    });
                    message.success('配置已回滚');
                    setHistoryOpen(false);
                    setHistoryVersions([]);
                    setHistoryConfig(null);
                    await loadConfigs();
                },
            });
        },
        [historyConfig, loadConfigs, message, modal]
    );

    const openHistoryDrawer = useCallback(
        async (record: RealtimeConfig) => {
            setHistoryConfig(record);
            setHistoryVersions([]);
            setHistoryOpen(true);
            setHistoryLoading(true);
            try {
                const versions = await RealtimeConfigService.listRealtimeConfigHistory(getConfigIdentity(record));
                setHistoryVersions(versions);
            } catch (error) {
                message.error(getErrorMessage(error, '获取历史版本失败'));
            } finally {
                setHistoryLoading(false);
            }
        },
        [message]
    );

    const columns = useMemo<ColumnsType<RealtimeConfig>>(
        () => [
            {
                title: 'Key',
                dataIndex: 'key',
                width: 360,
                ellipsis: true,
                render: (value: string, record) => (
                    <Space className="admin-config-key-cell" size={4}>
                        <Tooltip title={value}>
                            <Button
                                type="link"
                                className="admin-config-key-button"
                                onClick={() => void openEditDrawer(record)}
                            >
                                {value}
                            </Button>
                        </Tooltip>
                        <Tooltip title="复制 Key">
                            <Button
                                size="small"
                                type="text"
                                icon={<CopyOutlined />}
                                aria-label="复制 Key"
                                onClick={() => void copyText(value, 'Key')}
                            />
                        </Tooltip>
                    </Space>
                ),
            },
            {
                title: '类型',
                dataIndex: 'valueType',
                width: 100,
            },
            {
                title: '值',
                dataIndex: 'value',
                ellipsis: true,
                render: (value) => {
                    const text = stringifyConfigValue(value);
                    return (
                        <Tooltip title={text}>
                            <Text type="secondary" className="admin-config-table-text">
                                {text}
                            </Text>
                        </Tooltip>
                    );
                },
            },
            {
                title: '操作',
                width: 220,
                render: (_, record) => (
                    <Space>
                        <Button size="small" onClick={() => void openEditDrawer(record)}>
                            编辑
                        </Button>
                        <Button size="small" onClick={() => void openHistoryDrawer(record)}>
                            历史
                        </Button>
                        <Popconfirm
                            title="确认删除配置？"
                            description="删除后会让运行时读不到该配置。"
                            onConfirm={() => void deleteConfig(record)}
                        >
                            <Button size="small" danger>
                                删除
                            </Button>
                        </Popconfirm>
                    </Space>
                ),
            },
        ],
        [copyText, deleteConfig, openEditDrawer, openHistoryDrawer]
    );

    const historyColumns = useMemo<ColumnsType<RealtimeConfigVersion>>(
        () => [
            {
                title: '版本',
                dataIndex: 'version',
                width: 90,
                render: (value: number) => `v${value}`,
            },
            {
                title: '类型',
                dataIndex: 'valueType',
                width: 100,
            },
            {
                title: '值',
                dataIndex: 'value',
                ellipsis: true,
                render: (value?: string | null) => <Text type="secondary">{value ?? ''}</Text>,
            },
            {
                title: '更新时间',
                dataIndex: 'updateTime',
                width: 180,
            },
            {
                title: '操作',
                width: 90,
                render: (_, record) => (
                    <Button size="small" onClick={() => rollbackConfig(record)}>
                        回滚
                    </Button>
                ),
            },
        ],
        [rollbackConfig]
    );

    return (
        <div className="realtime-config-page">
            <Card
                className="realtime-config-panel"
                title="配置管理中心"
                extra={
                    <Space>
                        <Button onClick={() => void loadConfigs()}>
                            刷新
                        </Button>
                        <Button type="primary" onClick={openCreateDrawer}>
                            新增配置
                        </Button>
                    </Space>
                }
            >
                <div className="admin-config-filters">
                    <Space wrap>
                        <Tooltip title={filters.appKey}>
                            <Select
                                showSearch
                                allowClear
                                placeholder="App Key"
                                value={filters.appKey}
                                options={appKeyOptions}
                                style={{ width: 360 }}
                                onChange={(appKey) => {
                                    setFilters((current) => ({ ...current, appKey }));
                                    setPagination((current) => ({ ...current, current: 1 }));
                                }}
                            />
                        </Tooltip>
                        <Select
                            allowClear
                            placeholder="分类"
                            value={filters.category}
                            options={CATEGORY_OPTIONS}
                            style={{ width: 140 }}
                            onChange={(category) => {
                                setFilters((current) => ({ ...current, category }));
                                setPagination((current) => ({ ...current, current: 1 }));
                            }}
                        />
                        <Input.Search
                            allowClear
                            placeholder="搜索 Key"
                            style={{ width: 260 }}
                            onSearch={(key) => {
                                setFilters((current) => ({ ...current, key: key.trim() || undefined }));
                                setPagination((current) => ({ ...current, current: 1 }));
                            }}
                        />
                    </Space>
                </div>
                <Table
                    rowKey={(record) => `${record.appKey}:${record.category}:${record.key}`}
                    columns={columns}
                    dataSource={configs}
                    loading={loading}
                    pagination={pagination}
                    tableLayout="fixed"
                    onChange={(nextPagination) => {
                        void loadConfigs(nextPagination.current ?? 1, nextPagination.pageSize ?? 20, filters);
                    }}
                />
            </Card>

            <Drawer
                title={editingConfig ? '编辑配置' : '新增配置'}
                width={920}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                destroyOnClose
                extra={
                    <Popconfirm
                        title="确认保存配置？"
                        description="配置会立即影响运行时读取结果。"
                        onConfirm={() => void saveConfig()}
                    >
                        <Button type="primary" loading={saving}>
                            保存
                        </Button>
                    </Popconfirm>
                }
            >
                <Form form={form} layout="vertical" className="admin-config-form">
                    <div className="admin-category-form-grid">
                        {!editingConfig && (
                            <Form.Item
                                name="appKey"
                                label="App Key"
                                className="admin-config-form-row-full"
                                rules={[{ required: true, message: '请输入 App Key' }]}
                            >
                                <Select
                                    disabled
                                    options={appKeyOptions}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        )}
                        <Form.Item
                            name="key"
                            label="配置 Key"
                            className="admin-config-form-row-full"
                            rules={[{ required: true, message: '请输入配置 Key' }]}
                        >
                            <Input
                                title={drawerConfigKey}
                                disabled={!!editingConfig}
                                addonAfter={editingConfig ? (
                                    <Tooltip title="复制 Key">
                                        <Button
                                            size="small"
                                            type="text"
                                            icon={<CopyOutlined />}
                                            aria-label="复制 Key"
                                            onClick={() => void copyText(editingConfig.key, 'Key')}
                                        />
                                    </Tooltip>
                                ) : undefined}
                            />
                        </Form.Item>
                        <Form.Item name="category" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
                            <Select disabled={!!editingConfig} options={CATEGORY_OPTIONS} />
                        </Form.Item>
                        <Form.Item name="valueType" label="值类型" rules={[{ required: true, message: '请选择值类型' }]}>
                            <Select
                                options={VALUE_TYPE_OPTIONS}
                                onChange={(value: RealtimeConfigValueType) => {
                                    form.setFieldValue('valueText', getDefaultValueForType(value));
                                }}
                            />
                        </Form.Item>
                    </div>
                    <Form.Item name="valueText" label="配置值" rules={[{ required: true, message: '请输入配置值' }]}>
                        {selectedValueType === 'BOOL' ? (
                            <Radio.Group
                                options={[
                                    { label: 'true', value: 'true' },
                                    { label: 'false', value: 'false' },
                                ]}
                                optionType="button"
                                buttonStyle="solid"
                            />
                        ) : selectedValueType === 'INTEGER' ? (
                            <InputNumber className="admin-config-value-number" precision={0} placeholder="请输入整数" />
                        ) : selectedValueType === 'DOUBLE' ? (
                            <InputNumber className="admin-config-value-number" placeholder="请输入数字" />
                        ) : selectedValueType === 'MAP' || selectedValueType === 'LIST' ? (
                            <Input.TextArea
                                rows={12}
                                placeholder={selectedValueType === 'MAP' ? '{"enabled":true}' : '["a","b"]'}
                            />
                        ) : (
                            <Input placeholder="请输入字符串" />
                        )}
                    </Form.Item>
                    <Form.Item name="description" label="说明">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>

            </Drawer>

            <Drawer
                title={historyConfig ? `历史版本：${historyConfig.key}` : '历史版本'}
                width={760}
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                destroyOnClose
            >
                <section className="admin-config-history">
                    <Title level={5}>版本历史</Title>
                    <Table
                        size="small"
                        rowKey={(record) => String(record.id ?? record.version)}
                        columns={historyColumns}
                        dataSource={historyVersions}
                        loading={historyLoading}
                        pagination={false}
                    />
                </section>
            </Drawer>
        </div>
    );
}
